/**
 * storeClock.js -- reading a store's close on the distributor's clock.
 *
 * A store's end_date is a NAIVE wall-clock value in the distributor's
 * timezone; the store list rows name that zone as `schedule_timezone`
 * (resolved server-side: distributor, then platform default, then UTC).
 * The server's rule is Store.has_closed in api/src/models/store.py --
 * closed from the close datetime on, on that zone's clock. This is the
 * browser's reading of the same rule, for DISPLAY only; nothing here gates.
 *
 * The API sends the close as "2026-10-07 20:00:00" (the SAFRS JSON
 * provider's encoding of a naive datetime). Date.parse reads that in the
 * VIEWER's zone, which put an Eastern store's close 3h late on a Pacific
 * screen. So the fields are read as-is and never converted; a trailing
 * "Z" is ignored the same way, since store dates carry no zone of their own.
 */

const NAIVE_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?Z?)?$/;

const pad = (n) => String(n).padStart(2, "0");

function format(year, month, day, hour, minute, second) {
    return `${year}-${pad(month)}-${pad(day)} ${pad(hour)}:${pad(minute)}:${pad(second)}`;
}

/**
 * The wall clock a store date names, as "YYYY-MM-DD HH:mm:ss", or null.
 * Accepts the naive ISO forms the API and the editor use (date-only, "T"
 * or space separated, optional seconds/fraction/"Z").
 */
export function naiveWallClock(value) {
    if (value === null || value === undefined || value === "") return null;
    const m = NAIVE_RE.exec(String(value).trim());
    if (!m) return null;
    const [year, month, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
    const probe = new Date(Date.UTC(year, month - 1, day));
    if (probe.getUTCMonth() !== month - 1 || probe.getUTCDate() !== day) {
        return null;
    }
    return format(year, month, day, Number(m[4] || 0), Number(m[5] || 0), Number(m[6] || 0));
}

/**
 * The wall clock in `tz` at instant `now`, as "YYYY-MM-DD HH:mm:ss". With
 * no `tz`, the browser's own zone; with one Intl cannot read, UTC.
 */
export function wallClockInZone(tz, now = new Date()) {
    const opts = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
    };
    let formatter;
    try {
        formatter = new Intl.DateTimeFormat("en-US", tz ? { ...opts, timeZone: tz } : opts);
    } catch (err) {
        if (!(err instanceof RangeError)) throw err;
        // A zone Python accepts but Intl rejects ("Factory", "posixrules"):
        // read it as UTC, the server's last resort, rather than throw in
        // every row's render (/gate #3975 P2-2).
        formatter = new Intl.DateTimeFormat("en-US", { ...opts, timeZone: "UTC" });
    }
    const map = {};
    for (const p of formatter.formatToParts(now)) {
        if (p.type !== "literal") map[p.type] = p.value;
    }
    return format(map.year, map.month, map.day, map.hour, map.minute, map.second);
}

/**
 * Has a store with this close reached it, on `tz`'s clock?  Mirrors
 * Store.has_closed: closed from the close datetime onward. No (or an
 * unreadable) close date means no scheduled close. The fixed-width
 * "YYYY-MM-DD HH:mm:ss" strings compare correctly as text. Being a
 * wall-clock comparison, it differs from the server (which compares
 * instants) only for a close inside the repeated hour when DST ends.
 */
export function hasClosedInZone(endDate, tz, now = new Date()) {
    const close = naiveWallClock(endDate);
    if (!close) return false;
    return wallClockInZone(tz, now) >= close;
}
